import {
    cmpEncryptionChangePermissions,
    cmpEncryptionChangePermissionsDescription,
} from './modifyProtectionInfoBarCustomAction.locstring.json';
import { cancelButton } from 'owa-locstrings/lib/strings/cancelbutton.locstring.json';
import { okButton } from 'owa-locstrings/lib/strings/okbutton.locstring.json';
import loc from 'owa-localize';
import { createIRMData } from '../actions/protectMessage';
import setIRMData from '../actions/setIRMData';
import { ComposeViewState, getStore } from 'owa-mail-compose-store';
import { observer } from 'mobx-react-lite';
import { DefaultButton, PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { Dialog, DialogFooter, DialogType } from 'office-ui-fabric-react/lib/Dialog';
import { Dropdown, IDropdownOption } from 'office-ui-fabric-react/lib/Dropdown';
import { InfoBarCustomAction } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import { OwaModalProps, showModal } from 'owa-modal';
import { isFeatureEnabled } from 'owa-feature-flags';
import clearClassification from 'owa-mail-message-classification/lib/utils/clearClassification';
import classifyMessage from 'owa-mail-message-classification/lib/utils/classifyMessage';
import getEncryptionInfoBarMessageId from './getEncryptionInfoBarMessageId';
import removeInfoBarMessage from '../actions/removeInfoBarMessage';
import protectMessage from '../actions/protectMessage';

import * as React from 'react';

export function createModifyProtectionInfoBarCustomAction(
    viewState: ComposeViewState
): InfoBarCustomAction {
    return {
        text: loc(cmpEncryptionChangePermissions),
        action: () => {
            showModifyProtectionDialog(viewState);
        },
    };
}

async function showModifyProtectionDialog(viewState: ComposeViewState) {
    const templates = getStore().complianceConfigurations;
    const classifications = getStore().messageClassifications;
    const { IRMData, classificationViewState } = viewState;

    //TODO: Replace with default if exists.
    let defaultSelection = IRMData ? IRMData.RmsTemplateId : null;

    let dropdownOptions: IDropdownOption[] = [...templates.entries()].map(entry => ({
        key: entry[0],
        text: entry[1].TemplateName,
    }));

    if (isFeatureEnabled('cmp-message-classification') && classificationViewState) {
        const { messageClassification } = classificationViewState;
        if (!defaultSelection && messageClassification) {
            defaultSelection = messageClassification.Id;
        }

        const classificationDropdownOptions: IDropdownOption[] = [...classifications.entries()].map(
            entry => ({
                key: entry[0],
                text: entry[1].Name,
            })
        );

        dropdownOptions = classificationDropdownOptions.concat(dropdownOptions);
    }

    const WrappedComponent = ({ onDismiss }: OwaModalProps<string>) => (
        <SelectEncryptionTemplateModal
            defaultSelectedKey={defaultSelection}
            options={dropdownOptions}
            onDismiss={onDismiss}
        />
    );

    const [modalPromise] = showModal(WrappedComponent);
    modalPromise.then(newTemplate => {
        if (isFeatureEnabled('cmp-message-classification')) {
            if (newTemplate) {
                if (templates.get(newTemplate)) {
                    clearClassification(classificationViewState, viewState, viewState.composeId);
                    protectMessage(viewState, templates, newTemplate);
                } else if (classifications.get(newTemplate)) {
                    if (IRMData && IRMData.RmsTemplateId) {
                        removeInfoBarMessage(
                            viewState,
                            getEncryptionInfoBarMessageId(IRMData.RmsTemplateId)
                        );
                    }
                    setIRMData(viewState, null);
                    classifyMessage(
                        classificationViewState,
                        viewState,
                        classifications.get(newTemplate),
                        viewState.composeId
                    );
                }
            }
        } else if (newTemplate && templates.get(newTemplate)) {
            setIRMData(viewState, createIRMData(viewState, templates.get(newTemplate)));
        }
    });
}

interface SelectEncryptionTemplateProps extends OwaModalProps<string> {
    defaultSelectedKey: string;
    options: IDropdownOption[];
}

const SelectEncryptionTemplateModal = observer(function SelectEncryptionTemplateModal(
    props: SelectEncryptionTemplateProps
) {
    const [template, setTemplate] = React.useState(props.defaultSelectedKey);
    const dismissEmpty = React.useCallback(() => {
        props.onDismiss(null);
    }, [props.onDismiss]);

    const dismissWithTarget = React.useCallback(() => {
        props.onDismiss(template);
    }, [props.onDismiss, template]);

    const handleClick = React.useCallback(
        (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) => {
            setTemplate(item.key.toString());
        },
        []
    );

    return (
        <Dialog
            hidden={false}
            dialogContentProps={{
                type: DialogType.normal,
                title: loc(cmpEncryptionChangePermissions),
                subText: loc(cmpEncryptionChangePermissionsDescription),
            }}
            onDismiss={dismissEmpty}
            minWidth={'388px'}
            maxWidth={'inherit'}>
            <Dropdown
                defaultSelectedKey={props.defaultSelectedKey}
                onChange={handleClick}
                options={props.options}
            />
            <DialogFooter>
                <PrimaryButton text={loc(okButton)} onClick={dismissWithTarget} />
                <DefaultButton text={loc(cancelButton)} onClick={dismissEmpty} />
            </DialogFooter>
        </Dialog>
    );
});
